const DATE_ONLY_EXACT = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Format a date string to en-GB locale (DD/MM/YYYY)
 * Handles null, undefined, and invalid dates
 *
 * @param dateStr - Date string, Date object, null, or undefined
 * @returns Formatted date string or '-' if invalid/null
 */
export const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "-";
  try {
    // AG Grid wrappers can pass Date objects despite the signature.
    const date =
      typeof dateStr === "string" && DATE_ONLY_EXACT.test(dateStr.trim())
        ? parseDateOnly(dateStr)
        : new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-GB"); // DD/MM/YYYY
  } catch {
    return "-";
  }
};

/**
 * Date for a timestamp to display: API strings without an offset are UTC
 * (see parseInstantUtc); a bare 'YYYY-MM-DD' stays on its calendar day.
 */
function toTimestampDate(value: string | Date): Date {
  if (typeof value !== "string") return new Date(value);
  if (DATE_ONLY_EXACT.test(value.trim())) return parseDateOnly(value);
  return parseInstantUtc(value);
}

/**
 * Format a date string to en-GB locale with time (DD/MM/YYYY HH:MM)
 *
 * @param dateStr - Date string, Date object, null, or undefined
 * @returns Formatted date-time string or '-' if invalid/null
 */
export const formatDateTime = (
  dateStr: string | Date | null | undefined,
): string => {
  if (!dateStr) return "-";

  try {
    const date = toTimestampDate(dateStr);
    if (isNaN(date.getTime())) return "-";
    const datePart = date.toLocaleDateString("en-GB");
    const timePart = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${datePart} ${timePart}`;
  } catch {
    return "-";
  }
};

/**
 * Format for notes: dd mmm yy HH:mm (e.g. 19 Jan 26 10:17)
 */
export const formatNoteDateTime = (
  dateStr: string | Date | null | undefined,
): string => {
  if (!dateStr) return "-";
  try {
    const date = toTimestampDate(dateStr);
    if (isNaN(date.getTime())) return "-";
    const day = date.getDate().toString().padStart(2, "0");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day} ${month} ${year} ${hours}:${minutes}`;
  } catch {
    return "-";
  }
};

export function formatTaskTimestamps(task: {
  status: string;
  created_at: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
}): string {
  if (task.status === "completed") {
    return formatNoteDateTime(task.completed_at ?? task.updated_at);
  }
  return formatNoteDateTime(task.created_at);
}

const HAS_TZ =
  /(?:[zZ]|[+-]\d{2}(?::?\d{2})?|UTC\b)$/;
const NAIVE_ISO =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?$/;

/**
 * Parse an API instant where timestamps may be UTC but omit the `Z` suffix.
 * ECMAScript treats timezone-less ISO strings as local time, which skews
 * relative time in UK summer vs UTC-stored values.
 */
export function parseInstantUtc(iso: string): Date {
  if (!iso || typeof iso !== "string") return new Date(NaN);
  const t = iso.trim();
  if (!t) return new Date(NaN);
  if (HAS_TZ.test(t)) return new Date(t);
  const normalized = t.includes("T") ? t : t.replace(" ", "T");
  if (NAIVE_ISO.test(normalized)) return new Date(`${normalized}Z`);
  return new Date(t);
}

const DATE_ONLY_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

/** Local-midnight Date for a calendar day, or Invalid Date if the day does not exist. */
function localCalendarDate(year: number, month: number, day: number): Date {
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return new Date(NaN);
  }
  return date;
}

/**
 * Parse a calendar date from the API ('YYYY-MM-DD', or the date prefix of
 * 'YYYY-MM-DDTHH:mm…') as LOCAL midnight on that day.
 * `new Date('YYYY-MM-DD')` is UTC midnight, which is the previous day west of UTC.
 * Other formats fall back to `new Date(value)`.
 */
export function parseDateOnly(value: string | Date): Date {
  if (value instanceof Date) return new Date(value.getTime());
  if (!value || typeof value !== "string") return new Date(NaN);
  const t = value.trim();
  const match = DATE_ONLY_PREFIX.exec(t);
  if (match) {
    return localCalendarDate(
      Number(match[1]),
      Number(match[2]),
      Number(match[3]),
    );
  }
  return new Date(t);
}

/** 'YYYY-MM-DD' from the date's local calendar fields; '' for an invalid date. */
export function toDateOnlyString(date: Date): string {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  const y = date.getFullYear().toString().padStart(4, "0");
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const d = date.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Parse typed date text: UK day-first 'dd/mm/yyyy' (also '-' or '.'
 * separators) or ISO 'YYYY-MM-DD…'. Returns local midnight, or null when the
 * text is not one of those formats or is not a real date. Never reads
 * numeric dates US-style.
 */
export function parseUkDateText(value: string): Date | null {
  if (!value || typeof value !== "string") return null;
  const t = value.trim();
  const uk = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(t);
  const date = uk
    ? localCalendarDate(Number(uk[3]), Number(uk[2]), Number(uk[1]))
    : DATE_ONLY_PREFIX.test(t)
      ? parseDateOnly(t)
      : null;
  return date && !isNaN(date.getTime()) ? date : null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
/** Day 0 of the Excel 1900 date system once its fake 29 Feb 1900 is skipped. */
const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);

/**
 * Excel (1900 date system) serial → local midnight on that calendar day.
 * Computed in UTC so DST and the viewer's offset cannot move the day.
 * Accurate for serials from 61 (1 Mar 1900) onwards; earlier serials are
 * off by one because of Excel's fictitious 29 Feb 1900.
 */
export function excelSerialToDate(serial: number): Date | null {
  if (typeof serial !== "number" || !Number.isFinite(serial)) return null;
  const utc = new Date(EXCEL_EPOCH_UTC + Math.floor(serial) * MS_PER_DAY);
  if (isNaN(utc.getTime())) return null;
  return localCalendarDate(
    utc.getUTCFullYear(),
    utc.getUTCMonth() + 1,
    utc.getUTCDate(),
  );
}

function isExcelBlankDate(date: Date): boolean {
  return (
    date.getFullYear() === 1900 && date.getMonth() === 0 && date.getDate() === 1
  );
}

/**
 * Convert an Excel upload cell (serial number, Date, or text) to a local
 * midnight Date, or null when blank/unparseable. Text is read as UK
 * 'dd/mm/yyyy' or ISO 'YYYY-MM-DD'. 0, 1 and 01/01/1900 are treated as blank.
 */
export function excelCellToDate(value: unknown): Date | null {
  let date: Date | null = null;
  if (typeof value === "number") {
    if (value > 1000) date = excelSerialToDate(value);
  } else if (value instanceof Date) {
    date = isNaN(value.getTime()) ? null : value;
  } else if (typeof value === "string") {
    date = parseUkDateText(value);
  }
  if (!date || isExcelBlankDate(date)) return null;
  return date;
}
